import { defineStore } from "pinia";

import { useApi } from "@/composables/useApi";
import { useReleaseStore } from "@/views/releaser/store";

export interface Notification {
  id: string;
  type: string;
  title: string;
  date: string;
  link: string;
  seen: boolean;
}

interface NotificationsState {
  notifications: Notification[];
}

const LOCAL_STORAGE_KEY = "seen-notification-ids";

const getSeenNotificationIdsFromLocalStorage = () => {
  const data = localStorage.getItem(LOCAL_STORAGE_KEY);
  if (!data) return new Set();

  return new Set(JSON.parse(data));
};

const withSeenFromLocalStorage = (notifications: Notification[]) => {
  const seenNotificationIds = getSeenNotificationIdsFromLocalStorage();
  return notifications.map((notification) => {
    notification.seen = seenNotificationIds.has(notification.id);
    return notification;
  });
};

const saveSeenNotificationIdsToLocalStorage = (
  notifications: Notification[],
) => {
  const seenNotificationIds = getSeenNotificationIdsFromLocalStorage();
  notifications
    .filter((notification) => notification.seen === true)
    .forEach((notification) => {
      seenNotificationIds.add(notification.id);
    });
  localStorage.setItem(
    LOCAL_STORAGE_KEY,
    JSON.stringify(Array.from(seenNotificationIds)),
  );
};

export const useNotificationsStore = defineStore("notifications", {
  state: (): NotificationsState => ({ notifications: [] }),
  actions: {
    fetchNotifications() {
      const releaseStore = useReleaseStore();
      const apiClient = useApi();
      releaseStore.fetchReleases(apiClient);
      releaseStore.$subscribe(() => {
        this.notifications = releaseStore.getAsNotifications();
        if (this.notifications) {
          return {
            notifications: withSeenFromLocalStorage(this.notifications),
          };
        }
      });
    },
    markAllAsSeen() {
      this.notifications.forEach((notification: Notification) => {
        notification.seen = true;
      });
      saveSeenNotificationIdsToLocalStorage(this.notifications);
    },
    markAsSeen(id?: string | undefined) {
      if (!id) return;
      this.notifications.forEach((notification: Notification) => {
        if (notification.id === id) {
          notification.seen = true;
        }
      });
      saveSeenNotificationIdsToLocalStorage(this.notifications);
    },
  },
  getters: {
    getUnseenCount: (state: NotificationsState) => () =>
      state.notifications.filter((notification) => !notification.seen).length,

    getUnseenNotifications: (state: NotificationsState) => () =>
      state.notifications
        .filter((notification) => !notification.seen)
        .sort((a, b) => b.date.localeCompare(a.date)),
  },
});
